/**
 * Shipping a device out on trial, and watching it come back.
 *
 * Distinct from `device-shipment.type.ts`, which describes ONE LEG of a journey
 * as typed in by hand on the Devices table. A trial spans both legs and has one
 * recipient, one duration and one payment, so it is its own thing.
 */

/** Who the devices are going to. A clinic is a department. */
export enum ShipmentRecipientType {
  Patient = 'PATIENT',
  Department = 'DEPARTMENT',
}

/**
 * Where a trial has got to.
 *
 * Most of these are driven by the carrier rather than by anyone clicking
 * anything, which is why they read as observations rather than actions.
 */
export enum ShipmentOrderStatus {
  Draft = 'DRAFT',
  /** Created on Easyship; the label is bought on Easyship's own site. */
  AwaitingLabel = 'AWAITING_LABEL',
  LabelsPurchased = 'LABELS_PURCHASED',
  InTransit = 'IN_TRANSIT',
  Delivered = 'DELIVERED',
  OnTrial = 'ON_TRIAL',
  CollectionDue = 'COLLECTION_DUE',
  Returning = 'RETURNING',
  Returned = 'RETURNED',
  Exception = 'EXCEPTION',
  Cancelled = 'CANCELLED',
  Failed = 'FAILED',
}

export const SHIPMENT_ORDER_STATUS_LABELS: Record<string, string> = {
  [ShipmentOrderStatus.Draft]: 'Draft',
  [ShipmentOrderStatus.AwaitingLabel]: 'Buy label on Easyship',
  [ShipmentOrderStatus.LabelsPurchased]: 'Ready to send',
  [ShipmentOrderStatus.InTransit]: 'In transit',
  [ShipmentOrderStatus.Delivered]: 'Delivered',
  [ShipmentOrderStatus.OnTrial]: 'With the patient',
  [ShipmentOrderStatus.CollectionDue]: 'Collection due',
  [ShipmentOrderStatus.Returning]: 'Returning',
  [ShipmentOrderStatus.Returned]: 'Returned',
  [ShipmentOrderStatus.Exception]: 'Needs attention',
  [ShipmentOrderStatus.Cancelled]: 'Cancelled',
  [ShipmentOrderStatus.Failed]: 'Failed',
};

/**
 * Which visual weight a status carries.
 *
 * `Exception` and `Failed` are the only ones that mean somebody has to do
 * something, so they are the only ones that get an alarming colour. Everything
 * else is the parcel making normal progress and should not look like a problem.
 */
export function shipmentStatusTone(
  status?: string | null
): 'neutral' | 'progress' | 'good' | 'warn' {
  switch (status) {
    case ShipmentOrderStatus.Exception:
    case ShipmentOrderStatus.Failed:
      return 'warn';
    case ShipmentOrderStatus.Returned:
    case ShipmentOrderStatus.Delivered:
      return 'good';
    case ShipmentOrderStatus.Draft:
    case ShipmentOrderStatus.Cancelled:
      return 'neutral';
    default:
      return 'progress';
  }
}

/** A carrier's own status word, normalised. */
export const CARRIER_STATUS_LABELS: Record<string, string> = {
  created: 'Label created',
  in_transit: 'In transit',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  exception: 'Exception',
  returned: 'Returned to sender',
  cancelled: 'Cancelled',
  unknown: 'Update received',
};

/** One box the devices are being sent in. */
export interface PackedParcel {
  boxTypeId: number;
  boxCode: string;
  boxLabel: string;
  deviceIds: number[];
  deviceType: string;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  totalWeightKg: number;
  totalWattHours: number | null;
  containsBatteryPi967: boolean;
  declaredValuePerUnit: number;
  declaredCurrency: string;
  customsDescription: string;
}

export interface PackingProblem {
  deviceType?: string;
  deviceIds?: number[];
  message: string;
}

export interface PackingResult {
  parcels: PackedParcel[];
  problems: PackingProblem[];
  warnings: string[];
  /** One per device type in the selection — last time's answer, or blanks. */
  packingInputs?: PackingInput[];
  /** The boxes the lab has, to choose from. */
  boxes?: BoxOption[];
  /** Whether a default shipping origin exists. Without one, no quote is possible. */
  hasOrigin?: boolean;
  /** The sites the lab can ship from, default first. */
  origins?: ShipFromOption[];
  /** Easyship's customs categories, for the picker. */
  itemCategories?: ItemCategory[];
}

/** A site the ship form can send from. */
export interface ShipFromOption {
  id: number;
  label: string;
  city: string;
  state: string | null;
  postalCode: string;
  countryAlpha2: string;
  isDefault: boolean;
  verificationStatus?: 'VERIFIED' | 'UNVERIFIED' | 'NOT_DELIVERABLE';
}

/** What the item IS, for customs. Easyship refuses a quote without one. */
export interface ItemCategory {
  id: number;
  name: string;
}

/**
 * The dimensions for one device type, as the ship form asks for them.
 *
 * The admin is asked every time — they are the one holding the device — but with
 * whatever was entered last time already filled in, so a repeat shipment is a
 * glance rather than a re-measurement.
 */
export interface DeviceSpecInput {
  deviceType: string;
  lengthCm: number | string;
  widthCm: number | string;
  heightCm: number | string;
  weightKg: number | string;
  wattHours?: number | string | null;
  containsBatteryPi967: boolean;
  declaredValue?: number | string | null;
  declaredCurrency?: string | null;
  customsDescription?: string | null;
  /**
   * Easyship's category id. Either this or an HS code is MANDATORY — a quote is
   * refused outright without one. 6 is "Accessory (with battery)".
   */
  itemCategoryId?: number | null;
  hsCode?: string | null;
  boxTypeId?: number;
  units?: number;
}

/** What the server remembers about a device type, for prefilling the form. */
export interface PackingInput {
  deviceType: string;
  spec: Partial<DeviceSpecInput> | null;
  rule: { boxTypeId: number; units: number } | null;
  /** False when nobody has shipped this type before — the fields start empty. */
  known: boolean;
}

export interface BoxOption {
  id: number;
  code: string;
  label: string;
  innerLengthCm: string | number;
  innerWidthCm: string | number;
  innerHeightCm: string | number;
  maxWeightKg: string | number;
  tareWeightKg: string | number;
}

/**
 * Something that has to be fixed before the shipment can be booked.
 *
 * `field` names the input it belongs to so the form can point at it, rather than
 * dumping a list of sentences above a form and leaving the reader to work out
 * which box is wrong.
 */
export interface ShippingProblem {
  field: string;
  message: string;
  actual?: number;
  limit?: number;
}

/** The address a courier needs. Field limits are carrier label limits. */
export interface ShippingContact {
  contactName?: string | null;
  companyName?: string | null;
  phone?: string | null;
  email?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  countryAlpha2?: string | null;
}

/** Carrier label limits — the same numbers the API enforces. */
export const SHIPPING_FIELD_LIMITS = {
  contactName: 22,
  companyName: 27,
  line1: 35,
  line2: 35,
  phone: 20,
  email: 50,
} as const;

export interface QuoteRequest {
  deviceIds: number[];
  /** What the admin confirmed in the form. Also written back for next time. */
  specs?: DeviceSpecInput[];
  /** The site it ships from. Left out, the default. */
  originId?: number | null;
  /**
   * Put a prepaid return label in the box. Off makes it one-way — for
   * destinations no courier collects from; delivery is then the end of it.
   */
  withReturn?: boolean;
  recipient: {
    type: ShipmentRecipientType;
    patientId?: number | null;
    departmentId?: number | null;
    contact: ShippingContact;
  };
  parcels?: PackedParcel[];
}

/** One line of a price, as the courier charges it. Amounts are exact decimal strings. */
export interface PriceLine {
  label: string;
  amount: string;
}

export type CourierBadge = 'CHEAPEST' | 'FASTEST' | 'BEST_VALUE';

export const COURIER_BADGE_LABELS: Record<CourierBadge, string> = {
  CHEAPEST: 'Cheapest',
  FASTEST: 'Fastest',
  BEST_VALUE: 'Best value',
};

/**
 * One courier service, priced for the whole trial.
 *
 * Every field comes from Easyship's rates response; nothing here is estimated
 * on this side. `total` is what Dormotech pays — every delivery label plus every
 * return label — and `notIncluded` says in words what is not in it.
 */
export interface CourierOption {
  courierServiceId: string;
  courierName: string;
  serviceName: string;
  logoUrl: string | null;
  minDays: number | null;
  maxDays: number | null;
  tracking: { rating: number | null; label: string; confirmsDelivery: boolean };
  handover: {
    dropoff: boolean;
    freePickup: boolean;
    paidPickup: boolean;
    minimumPickupFee: string | null;
    label: string;
  };
  currency: string;
  total: string;
  totalMinor: number;
  delivery: { total: string; lines: PriceLine[]; perParcelMinor: number[] };
  notIncluded: string[];
  ranks: { cost: number | null; speed: number | null; value: number | null };
  badges: CourierBadge[];
  warnings: string[];
  remarks: string | null;
}

/** The return label every option uses — chosen once, the same for all. */
export interface ReturnChoice {
  courierServiceId: string;
  courierName: string;
  serviceName: string;
  total: string;
  perParcelMinor: number[];
  lines: PriceLine[];
  trackingLabel: string;
}

export interface QuoteResult {
  ok: true;
  parcels: PackedParcel[];
  warnings: string[];
  currency: string | null;
  options: CourierOption[];
  /** Whether the prices include a return label. */
  withReturn?: boolean;
  returnChoice: ReturnChoice | null;
  recommendedId: string | null;
  recommendation: string | null;
  rankSource: 'easyship' | 'computed';
  unavailable: Array<{ name: string; reason: string }>;
  emptyReason: string | null;
  quotedAt: string;
  expiresAt: string;
  /** Sent back with the purchase, which verifies it. */
  quoteToken: string | null;
}

/** A refusal: something to fix in the form, or a price that moved. */
export interface ShippingRefusal {
  ok: false;
  code: string;
  problems?: ShippingProblem[];
  message?: string;
  retryable?: boolean;
  /** PRICE_CHANGED / OPTION_UNAVAILABLE carry the fresh comparison. */
  previousTotal?: string;
  currentTotal?: string | null;
  currency?: string;
  quote?: QuoteResult;
}

export interface CreateOrderRequest extends QuoteRequest {
  trialDays: number;
  courierServiceId: string;
  quoteToken: string;
  /** One per Confirm attempt — a repeated request returns the first one's trial. */
  requestKey: string;
  /** Save the address the trial goes to as the patient's own, once labels are bought. */
  updatePatientAddress?: boolean;
  /** Create the shipment on Easyship and buy nothing: the label is bought on Easyship's site. */
  labelOnEasyship?: boolean;
}

/** How the purchase went, parcel by parcel. */
export interface PurchaseReport {
  parcels: number;
  purchased: number;
  failed: number;
  returnMissing: number;
  /** Shipments created on Easyship whose label is to be bought on Easyship's site. */
  awaitingLabel?: number;
  messages: string[];
}

export interface CreateOrderResult extends ShipmentOrderDetail {
  ok: true;
  replayed: boolean;
  purchase: PurchaseReport | null;
  /** Whether the patient's own address was updated, when that was asked for. */
  patientAddressUpdated?: boolean;
}

// ─────────────────────────────────────────────── address deliverability ──

/**
 * Whether Easyship can deliver to an address and bring the device back from it.
 *
 * Decided by the API, from the postcode, city, state and country — not the
 * street, which Easyship's pricing does not check.
 */
export type DeliverabilityStatus = 'DELIVERABLE' | 'NOT_DELIVERABLE' | 'UNKNOWN' | 'INCOMPLETE' | 'INVALID';

export interface DeliverabilityResult {
  status: DeliverabilityStatus;
  /** For NOT_DELIVERABLE: nothing can go out, or nothing can come back. */
  reason: 'NO_OUTBOUND' | 'NO_RETURN' | null;
  /** A definite answer young enough to act on. Only these prefill a shipment. */
  fresh: boolean;
  checkedAt: string | null;
  problems: ShippingProblem[];
  /** The address as it was judged: trimmed, ISO country, US state as its code. */
  address: ShippingContact;
  message: string;
}

/** A patient as the ship dialog lists them. Never carries the street. */
export interface ShippablePatient {
  id: number;
  patientId: string | null;
  firstName: string | null;
  lastName: string | null;
  departmentId: number | null;
  place: string | null;
  status: DeliverabilityStatus;
  reason: 'NO_OUTBOUND' | 'NO_RETURN' | null;
  fresh: boolean;
  checkedAt: string | null;
  message: string;
}

/**
 * One patient checked now, from one ship-from site. `contact` — who it is for —
 * always comes from the patient; `prefill` — the address — only when Easyship
 * can deliver there.
 */
export interface PatientShippingCheck extends DeliverabilityResult {
  patientId: number;
  originId: number | null;
  contact: Pick<ShippingContact, 'contactName' | 'phone' | 'email'>;
  prefill: ShippingContact | null;
}

/** One page of the ship dialog's patient list. */
export interface ShippablePatientPage {
  items: ShippablePatient[];
  /** Every patient matching the search, not only this page. */
  total: number;
  offset: number;
  limit: number;
  /** The ship-from site the statuses are about. */
  originId: number | null;
}

/** Shipping behaviour fixed in the API's code (`ShippingFeatures`), not set on a screen. */
export interface ShippingPreferences {
  /**
   * Dormotech Direct: check a patient's address with Easyship on the patient
   * form. Off for now. The ship dialog checks the patient it ships to either way.
   */
  patientAddressCheck: boolean;
}

export const DELIVERABILITY_LABELS: Record<DeliverabilityStatus, string> = {
  DELIVERABLE: 'Easyship-deliverable',
  NOT_DELIVERABLE: 'Not deliverable',
  UNKNOWN: 'Not checked',
  INCOMPLETE: 'Address incomplete',
  INVALID: 'Address invalid',
};

/** The comparison re-run for a trial that already exists, beside what was booked. */
export interface RequoteResult extends QuoteResult {
  booked: {
    courierServiceId: string | null;
    courierName: string | null;
    quotedTotal: string | null;
    totalCost: string | null;
    currency: string | null;
    quotedAt: string | null;
  };
}

/** What the trial kept of the comparison the admin confirmed. */
export interface QuoteSnapshot {
  quotedAt: string;
  expiresAt: string;
  currency: string;
  chosenId: string;
  recommendedId: string | null;
  options: Array<{
    id: string;
    courier: string;
    service: string;
    minDays: number | null;
    maxDays: number | null;
    tracking: number | null;
    total: string;
    badges: CourierBadge[];
  }>;
  /** `null` for a one-way shipment. */
  return: { id: string; courier: string; service: string; total: string } | null;
}

export interface ShipmentOrder {
  /** `false` for a one-way shipment: no return label, no trial clock. */
  withReturn?: boolean;
  id: number;
  status: ShipmentOrderStatus | string;
  recipientType: ShipmentRecipientType | string;
  recipientContactName: string;
  recipientCompanyName?: string | null;
  recipientCity?: string;
  recipientCountryAlpha2?: string;
  trialDays: number;
  deliveredAt?: string | null;
  dueBackAt?: string | null;
  returnedAt?: string | null;
  totalCost?: string | null;
  currency?: string | null;
  createdAt?: string;
  /** How many devices went out in this trial, summed across its parcels. */
  deviceCount?: number;
  /**
   * The outbound tracking number, lifted from the legs so the list can show it.
   * `null` until the labels are bought — a trial that was never confirmed, or
   * whose purchase failed, genuinely has no number.
   */
  trackingNumber?: string | null;
  /** Parcels whose label purchase failed. Non-zero means the trial never left. */
  failedCount?: number;
  /** Parcels with a delivery label but no return label — paid for, and needing a person. */
  returnMissingCount?: number;
  /** The courier service the admin chose, and what it was quoted at. */
  courierName?: string | null;
  quotedTotal?: string | null;
  quote?: QuoteSnapshot | null;
  cancelledAt?: string | null;
  lastError?: string | null;
  [extra: string]: any;
}

/** One leg of a trial, with its labels. */
export interface ShipmentOrderLeg {
  id: number;
  device_id: number;
  direction: string | null;
  carrier: string | null;
  tracking_number: string | null;
  est_delivery: string | null;
  status: string | null;
  status_detail: string | null;
  status_at: string | null;
  location: string | null;
  label_url: string | null;
  battery_form_url: string | null;
  return_label_url: string | null;
  return_tracking_number: string | null;
  cost: string | null;
  return_cost: string | null;
  currency: string | null;
  purchase_status: string | null;
  /** The shipment on Easyship, as its dashboard lists it — to find it there and buy its label. */
  easyship_shipment_id?: string | null;
}

/** One checkpoint the carrier reported. */
export interface ShipmentTimelineEvent {
  id: string;
  deviceShipmentId: number;
  status: string;
  statusDetail: string | null;
  carrier: string | null;
  location: string | null;
  occurredAt: string | null;
  createdAt: string;
  /**
   * Which of the trial's two parcels this scan belongs to.
   *
   * A trial is an outbound journey and a homeward one, and their checkpoints
   * share a single timeline. Without this the list reads as impossible —
   * "Delivered" followed by "In transit" and "Delivered" again — because
   * nothing says the second pair is a different parcel.
   */
  legDirection?: 'OUTBOUND' | 'RETURN';
}

/** A parcel of a shipment that left and is not back yet: what "Mark received" offers. */
export interface ReceivableParcel {
  id: number;
  deviceSerials: string[];
}

export interface ShipmentOrderDetail {
  order: ShipmentOrder;
  legs: ShipmentOrderLeg[];
  timeline: ShipmentTimelineEvent[];
  receivable?: ReceivableParcel[];
}

/** Human label for a carrier status, falling back to whatever it said. */
export function carrierStatusLabel(status?: string | null, detail?: string | null): string {
  if (!status) {
    return detail ?? '';
  }
  return CARRIER_STATUS_LABELS[status] ?? detail ?? status;
}

/**
 * Money as the API returns it — an exact decimal string — formatted for reading.
 *
 * Display only: the number is never added to anything here, so converting it
 * for `Intl` cannot introduce a rounding error into a total. `Intl` is what
 * gets the currency right — two decimals for USD, none for JPY, three for KWD.
 */
export function formatMoney(amount?: string | number | null, currency?: string | null): string {
  if (amount === null || amount === undefined || amount === '') {
    return '—';
  }
  const value = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(value)) {
    return '—';
  }
  if (currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
    } catch {
      // An unknown code still shows the amount rather than nothing.
      return `${currency} ${value}`;
    }
  }
  return String(value);
}

/** "2 business days", "2–3 business days", or nothing when the courier gave no estimate. */
export function deliveryDaysLabel(minDays: number | null, maxDays: number | null): string {
  if (minDays === null && maxDays === null) {
    return 'No delivery estimate';
  }
  const min = minDays ?? maxDays;
  const max = maxDays ?? minDays;
  const span = min === max ? `${max}` : `${min}–${max}`;
  return `${span} business day${max === 1 ? '' : 's'}`;
}

/**
 * Days until a trial is due back — negative once it is overdue.
 *
 * `null` while the parcel has not arrived, because the clock starts on delivery
 * and a countdown from nothing would be a made-up number.
 */
export function daysUntilDue(dueBackAt?: string | null): number | null {
  if (!dueBackAt) {
    return null;
  }
  const due = new Date(dueBackAt).getTime();
  if (!Number.isFinite(due)) {
    return null;
  }
  return Math.ceil((due - Date.now()) / (24 * 60 * 60 * 1000));
}

/**
 * How a trial's due date should read.
 *
 * `null` while the parcel has not arrived — the clock starts on delivery, so a
 * countdown before then would be a made-up number. Overdue is stated in days
 * rather than as a date, because "3 days overdue" is the thing somebody acts on.
 */
export function dueBackLabel(dueBackAt?: string | null): { text: string; overdue: boolean } | null {
  const days = daysUntilDue(dueBackAt);
  if (days === null) {
    return null;
  }
  if (days < 0) {
    return { text: `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`, overdue: true };
  }
  if (days === 0) {
    return { text: 'Due back today', overdue: false };
  }
  return { text: `${days} day${days === 1 ? '' : 's'} left`, overdue: false };
}
